import { useState, useEffect } from 'react';
import { getUserBadges } from 'store/actions';
import { useDispatch } from 'react-redux';
import './AccountCredentials.css';
import { ReactComponent as CopyIcon } from './CopyIcon.svg';
import { ReactComponent as DownloadIcon } from './DownloadIcon.svg';

const THREE_MINUTES_MS = 3 * 60 * 1000;

const AccountCredentials = ({ eventId, userId }) => {
	const dispatch = useDispatch();

	const [badges, setBadges] = useState([]);
	const [currentTime, setCurrentTime] = useState(Date.now());
	const [copiedId, setCopiedId] = useState(null);

	useEffect(() => {
		dispatch(getUserBadges(eventId, userId))
			.then((fetchedBadges) => setBadges(fetchedBadges))
			.catch((err) => console.error('Failed to fetch badges', err));
	}, [dispatch, eventId, userId]);

	useEffect(() => {
		const intervalId = setInterval(() => setCurrentTime(Date.now()), 1000);

		return () => clearInterval(intervalId);
	}, []);

	const handleCopy = async (id, text) => {
		try {
			await navigator.clipboard.writeText(text);
			setCopiedId(id);
			setTimeout(() => setCopiedId(null), 1400);
		} catch (e) {
			console.error('Copy failed', e);
		}
	};

	const toDDMMYYYY = (iso) => {
		const d = new Date(iso); // uses environment timezone
		const dd = String(d.getDate()).padStart(2, '0');
		const mm = String(d.getMonth() + 1).padStart(2, '0');
		const yyyy = d.getFullYear();
		return `${dd}.${mm}.${yyyy}`;
	};

	return (
		<section className="badges-wrap">
			<h1 className="badges-heading">My Badges</h1>
			<ul className="badges">
				{badges
					.sort((a, b) => new Date(b.purchasedAt) - new Date(a.purchasedAt))
					.map((b) => {
						const purchasedTime = new Date(b.purchasedAt).getTime();
						const isBadgeReady =
							currentTime - purchasedTime >= THREE_MINUTES_MS;

						return (
							<li className="badge-card" key={b._id}>
								<a
									href={b.verificationLink}
									className="badge-left badge-title"
									target="_blank"
									rel="noopener noreferrer"
								>
									{b.name}
								</a>

								<div className="badge-right">
									<time className="badge-date" dateTime={b.purchasedAt}>
										{toDDMMYYYY(b.purchasedAt)}
									</time>

									<div className="badge-actions">
										<button
											type="button"
											className="action"
											onClick={() => handleCopy(b._id, b.verificationLink)}
											aria-label={`Copy URL for ${b.name}`}
										>
											<CopyIcon />
											<em>{copiedId === b._id ? 'Copied!' : 'Copy URL'}</em>
										</button>

										<a
											className="action"
											href={b.badgeImageLink}
											download={isBadgeReady ? '' : undefined}
											aria-label={`Download ${b.name}`}
											aria-disabled={!isBadgeReady}
											title={
												!isBadgeReady
													? 'Your badge is being generated and will be ready to download soon.'
													: undefined
											}
											onClick={(event) => {
												if (!isBadgeReady) {
													event.preventDefault();
												}
											}}
											style={
												isBadgeReady
													? undefined
													: {
															cursor: 'not-allowed',
															opacity: 0.5,
														}
											}
										>
											<DownloadIcon />
											<em>Download</em>
										</a>
									</div>
								</div>
							</li>
						);
					})}
			</ul>
		</section>
	);
};

export default AccountCredentials;
